import { EditorType } from '@/components/tiptap/types/index.enums';
import { type SectionEditorContext } from '@/models/analytics/features/notes';
import { createContext, useContext } from 'react';

export const EditorTypeContext = createContext<EditorType | undefined>(undefined);

function assertNever(value: never): never {
  throw new Error(`Unhandled EditorType: ${String(value)}`);
}

/**
 * Maps an `EditorType` to the section analytics `editor_context`. markdown-native
 * hosts the note and document editors today; section-less editors (and the
 * no-provider case) report `note`. Exhaustive by design — `assertNever` turns a
 * new `EditorType` into a compile error here rather than letting it silently
 * fall through to `note` and skew the analytics.
 */
export function toSectionEditorContext(editorType?: EditorType): SectionEditorContext {
  switch (editorType) {
    case EditorType.DOCUMENT:
      return 'document';
    case EditorType.TEMPLATE:
      return 'template';
    case EditorType.NOTE:
    case EditorType.ASK_AI:
    case EditorType.CONTEXT:
    case undefined:
      return 'note';
    default:
      return assertNever(editorType);
  }
}

/** The section analytics `editor_context` for the surrounding editor. */
export function useSectionEditorContext(): SectionEditorContext {
  return toSectionEditorContext(useContext(EditorTypeContext));
}
